import {
  cloneDeep, get, merge, findIndex, find, pick, assign, set, pickBy, identity, isNil,
} from 'lodash-es';
import {
  asapScheduler, Observable, of, throwError,
} from 'rxjs';
import {
  filter, map, startWith, tap, catchError,
} from 'rxjs/operators';
import {
  EventEmitter, Injectable, Output, Directive,
} from '@angular/core';

import { AnalyticsService, RestApiService } from '@ui-kit';
// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { LeanListing } from '../../public/homes/homes.helpers';
import { LeanFavourite, mapToLeanFavourite } from '../../public/favourites/shortlist.helpers';
import { STORAGE, StorageService } from './storage.service';
import { residencyOptions } from '../shared.helpers';
import { environment } from '../../../environments/environment';

export interface UserEvent {
  type: 'signIn' | 'signOut' | 'forceSignOut' | 'change' | 'favourite';
  data?: any;
}

const DEFAULT_APPLICANT = {
  income: 0,
  income_extra: 0,
  savings: 0,
};

export type Segment = P.Segment;

export type QualifierBreakdown = {
  hasBelowMinDeposit: boolean;
  hasIneligibleResidenceType: boolean;
  hasBelowMinIncome: boolean;
  isNotInterrestedInMortgageBooster: boolean;
}

@Directive()
@Injectable({
  providedIn: 'root',
})
// tslint:disable-next-line: directive-class-suffix
export class UserService {
  static DEFAULT_USER: Partial<P.User<P.Applicant>> = {
    ...DEFAULT_APPLICANT,
    application: {
      stage: 0,
      income: 0,
      income_extra: 0,
      deposit: 0,
      dependants: 0,
      significant_monthly_outgoings: 0,
      property_price: 0,
      isDefault: true,
      shortlist: [],
      loan: null,
      broker: null,
      brokerChoice: [],
      applicants: [DEFAULT_APPLICANT as P.User<P.Applicant>],
    } as Partial<P.Application>,
    preferences: {},
  };

  static TOKEN_KEY = 'current_user_token';

  static REFRESH_TOKEN_KEY = 'current_user_refresh_token';

  static CURRENT_KEY = 'current_user';

  static ORIGIN_KEY = 'current_user_origin';

  // tslint:disable-next-line: no-output-native
  @Output() change: EventEmitter<P.User<P.Applicant>> = new EventEmitter();

  @Output() event: EventEmitter<UserEvent> = new EventEmitter();

  get isFirstLogIn(): boolean {
    const lastLoginAtKey = `lastLoginAt_${this.current.id}`;

    const hasLastLoginAt = !!this.current.lastLoginAt;
    const hasLastLoginAttemptAt = !!this.current.lastLoginAttemptAt;

    const isFirstLogIn = this.signedIn
      && hasLastLoginAt
      && !hasLastLoginAttemptAt
      && !this.storage.get(STORAGE.LOCAL, lastLoginAtKey);

    if (this.signedIn) {
      this.storage.set(STORAGE.LOCAL, lastLoginAtKey, this.current.lastLoginAt);
    }

    return isFirstLogIn;
  }

  get eligibleForReferral(): boolean {
    const rightTime = this.current.application?.qualifier?.timeline?.answer === 'Now to 6 months';

    // const rightPlace = this.current.application?.qualifier?.home?.answer === 'Yes';

    return this.signedIn
      // is looking to buy "now"
      && rightTime
      // meets minimum deposit
      && this.current.application?.deposit >= 15000
      // is eligible for product
      && this.profileComplete?.total >= 1
      && this.current.application?.is_within_age_range
      && this.current.application?.has_steady_income_source
      && this.current.application?.has_clean_credit_record;
  }

  /**
   * signed in
   */
  get profileComplete(): {
    applicants: number[];
    account: number;
    total: number;
    } {
    const currentUser: any = this.current;

    const applicants = (currentUser.application?.applicants || []);

    const applicantsRequiredPercent = applicants.length;

    let applicantsCompletedPercent = 0;

    for (const [i, applicant] of applicants.entries()) {
      let requiredUserKeys = [
        'first_name',
        'last_name',
        // 'phone',
        'income',
        'savings',
        'employment_type',
        'residence_type',
      ];

      switch (applicant?.employment_type) {
        case 'Employed':
          requiredUserKeys = [
            ...requiredUserKeys,
            'income_basic',
            // 'income_bonus',
            // 'income_overtime',
            // 'income_commission',
          ];
          break;

        case 'Self-employed':

          requiredUserKeys = [
            ...requiredUserKeys,
            'self_employment_type',
          ];
          switch (applicant?.self_employment_type) {
            case 'Sole trader':

              requiredUserKeys = [
                ...requiredUserKeys,
                'income_net_profit_latest',
                'income_net_profit_previous',
              ];
              break;

            case 'Company director':
              requiredUserKeys = [
                ...requiredUserKeys,
                'income_salary_latest',
                'income_salary_previous',
                // 'income_dividends_latest',
                // 'income_dividends_previous',
              ];
              break;

            default:
              break;
          }
          break;

        default:
          break;
      }

      const requiredUserKeysWithValue = Object.keys(pickBy(pick(applicant, requiredUserKeys), identity));

      const applicantCompletedPercent = requiredUserKeysWithValue.length / requiredUserKeys.length;

      applicants[i] = applicantCompletedPercent;

      applicantsCompletedPercent += applicantCompletedPercent;
    }

    applicantsCompletedPercent /= applicantsRequiredPercent;

    const requiredAccountKeys = [
      'income',
      'deposit',
      'property_price',

      // 'dependants',
      // 'significant_monthly_outgoings',

      // 'has_steady_income_source',
      // 'has_clean_credit_record',
      // 'is_within_age_range',
    ];

    const account = currentUser.application;

    const requiredAccountKeysWithValue = Object.keys(pickBy(pick(account, requiredAccountKeys), (v) => !isNil(v)));

    const accountCompletedPercent = requiredAccountKeysWithValue.length / requiredAccountKeys.length;

    return {
      applicants,
      account: accountCompletedPercent,
      total: (applicantsCompletedPercent + accountCompletedPercent) / 2,
    };
  }

  /**
   * signed in
   */
  get signedIn(): boolean {
    return !!this.restApi.accreditation?.user?.id || !!this.current?.id;
  }

  get signedInObservable(): Observable<boolean> {
    // signed in state
    return this.event.pipe(
      filter((e) => e.type === 'signIn' || e.type === 'signOut'),
      map((e) => e.type === 'signIn'),
      startWith(this.signedIn),
    );
  }

  /**
   * is default
   */
  get isDefault(): boolean {
    return !!get(this.current, 'application.isDefault');
  }

  get isDefaultObservable(): Observable<boolean> {
    // signed in state
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.isDefault),
      startWith(this.isDefault),
    );
  }

  /**
   * favourites
   */
  get favourites(): LeanFavourite[] {
    return (this.current.application?.shortlist || []) // as LeanFavourite[];
      .map((favourite) => mapToLeanFavourite(favourite, undefined, undefined, this.favouritedListings));
  }

  get favouritesObservable(): Observable<LeanFavourite[]> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.favourites),
      startWith(this.favourites),
    );
  }

  get favouritedListings(): (string | P.Listing)[] {
    return (this.current?.application?.shortlist || [])
      .map((favourite) => (favourite?.listing as unknown as P.Listing)?.id || favourite?.listing);
  }

  /**
   * has favourites
   */
  get hasFavourites(): boolean {
    return this.current.application?.shortlist?.length > 0;
  }

  get hasFavouritesObservable(): Observable<boolean> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.hasFavourites),
      startWith(this.hasFavourites),
    );
  }

  get mip(): P.LoanFile[] {
    return this.current.application?.mip;
  }

  get mipObservable(): Observable<P.LoanFile[]> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.mip),
      startWith(this.mip),
    );
  }

  get preQualifies(): boolean {
    const { application } = this.current;

    const { qualifier }: any = application;

    const isInQualifiyingSegment = /Medium|High/.test(qualifier?.segment || '');

    const hasProductInterest = !!qualifier?.products?.answer;

    if (hasProductInterest) {
      const hasInterestInMortgageBoosterProduct = `${qualifier?.products?.answer || ''}`
        .split(',')
        .indexOf('mortgage_booster') > -1;

      return hasInterestInMortgageBoosterProduct
        && isInQualifiyingSegment;
    }

    return isInQualifiyingSegment;
  }

  get qualifiesForBookACall(): boolean {
    return false; // this.preQualifies;
  }

  /**
   * broker
   */
  get broker(): P.Broker {
    return this.current.application?.broker;
  }

  get brokerObservable(): Observable<P.Broker> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.broker),
      startWith(this.broker),
    );
  }

  /**
   * has broker
   */
  get hasBroker(): boolean {
    return !!this.current.application?.broker;
  }

  get hasBrokerObservable(): Observable<boolean> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.hasBroker),
      startWith(this.hasBroker),
    );
  }

  get qualifiesForBookACallWithBroker(): boolean {
    // const qualifiesForBookACall = this.qualifiesForBookACall;
    // added layer for broker call qualification
    const timeline = this.current.application?.qualifier?.timeline?.answer || '';

    return this.preQualifies
      && timeline === 'Now to 6 months';
  }

  get application(): Partial<P.Application> {
    return this.current?.application;
  }

  get status(): string {
    return this.current?.application?.status;
  }

  get statusObservable(): Observable<string> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.status),
      startWith(this.status),
    );
  }

  get budget(): number {
    const budget = this.current?.application?.property_price;
    const confirmedBudget = this.confirmedBudget;

    return confirmedBudget || budget;
  }

  get budgetObservable(): Observable<number> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.budget),
      startWith(this.budget),
    );
  }

  get confirmedBudget(): number {
    return this.current?.application?.confirmedBudget || 0;
  }

  get confirmedBudgetObservable(): Observable<number> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.confirmedBudget),
      startWith(this.confirmedBudget),
    );
  }

  /**
   * my home
   */
  get myHome(): LeanFavourite {
    return this.current?.application?.my_home as LeanFavourite;
  }

  /**
   * my home observable
   */
  get myHomeObservable(): Observable<LeanFavourite> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.myHome),
      startWith(this.myHome),
    );
  }

  /**
   * my home
   */
  get loan(): Partial<P.LoanApplication> {
    const loan = (this.current?.application as any)?.loan as P.LoanApplication;

    const leanLoan: Partial<P.LoanApplication> = loan
      ? pick(loan, [
        'id',
        'property',
        'progress',
        'completed',
        'completedDate',
        'createdBy',
        'supportingFiles',
        'otherFiles',
        'offerPackFile',
        'filesv2',
      ])
      : null;

    return leanLoan;
  }

  /**
   * my home observable
   */
  get loanObservable(): Observable<Partial<P.LoanApplication>> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.loan),
      startWith(this.loan),
    );
  }

  /**
   * wait list
   */
  get waitList(): P.LoanQueueSlot {
    return this.current?.application?.allocated_slot;
  }

  /**
   * is in the wait list
   */
  get inWaitList(): boolean {
    return !!this.waitList;
  }

  /**
   * my home
   */
  get hasMyHome(): boolean {
    return !!this.myHome;
  }

  /**
   * my home observable
   */
  get hasMyHomeObservable(): Observable<boolean> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.hasMyHome),
      startWith(this.hasMyHome),
    );
  }

  /**
   * loan
   */
  get hasLoan(): boolean {
    return !!this.loan;
  }

  /**
   * loan observable
   */
  get hasLoanObservable(): Observable<boolean> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.hasLoan),
      startWith(this.hasLoan),
    );
  }

  get referralLink(): string {
    return `${environment.urls.www}/?referrer=${this.current.referral}`;
  }

  /**
   * current user
   */
  get current(): P.User<P.Applicant> {
    return (
      this.storage.get(STORAGE.SESSION, UserService.CURRENT_KEY)
      || cloneDeep(UserService.DEFAULT_USER)
    );
  }

  set current(user: P.User<P.Applicant>) {
    if (user) {
      const applicantKeys = [
        'id',
        'email',
        'phone',
        'first_name',
        'last_name',
        'income',
        'income_extra',
        'savings',
        'referral',
        'preferences',
        'persistence_id',
        'verified',
        'lastLoginAt',
        'lastLoginAttemptAt',
        'createdAt',
        'dateOfBirth',

        'discovery_source',
        'residence_type',
        'employment_type',
        'self_employment_type',
        'income_basic',
        'income_bonus',
        'income_overtime',
        'income_commission',
        'income_net_profit_latest',
        'income_net_profit_previous',
        'income_salary_latest',
        'income_salary_previous',
        'income_dividends_latest',
        'income_dividends_previous',
      ];

      const keys = [
        ...applicantKeys,
        'application.id',
        'application.status',
        'application.stage',
        'application.applicants',
        'application.broker.id',
        'application.broker.logo_url',
        'application.broker.url',
        'application.broker.name',
        'application.broker.email',
        'application.broker.fca_number',
        'application.broker.phone',
        'application.broker.isInternal',
        'application.brokerChoice',
        'application.income',
        'application.income_extra',
        'application.deposit',
        'application.dependants',
        'application.significant_monthly_outgoings',
        'application.has_steady_income_source',
        'application.has_clean_credit_record',
        'application.is_within_age_range',
        'application.joint_application',
        'application.property_price',
        'application.isDefault',
        'application.shortlist',
        'application.percent_completed',
        'application.my_home',
        'application.loan',
        'application.qualifier',
        'application.tags',
        'application.referredAt',
        'application.referredSelf',
        'application.createdAt',
        'application.updatedAt',
        'application.createdBy',
        'application.broker_assessment',
        'application.confirmedBudget',
        'application.mip',
        'application.supportingFiles',
        'application.otherFiles',
        'signedApplicationFile',
        'signedInterestOnlyDeclarationFile',
      ];

      const shortlistKeys = [
        'id',
        'price',
        'status',
        'property_type',
        'surface_area',
        'price',
        'createdAt',
        'listing',
        'updatedAt',
        'can_generate_pea',
        'pea_downloaded',
        'arranged_viewing',
        'ready_to_offer', // TODO: should be offer_made
        'progress_towards_completion',
        'category',
      ];

      const leanUser = pick(user, keys);
      set(leanUser, 'application.applicants', (leanUser?.application?.applicants || [])
        .map((applicant) => pick(applicant, applicantKeys)));

      // set(leanUser, 'application.shortlist', (leanUser?.application?.shortlist || [])
      //   .map(favourite => pick(favourite, shortlistKeys)));

      // if (!!leanUser?.application?.my_home) {
      //   set(leanUser, 'application.my_home', mapToLeanFavourite(leanUser?.application?.my_home, null, null, this.favouritedListings));
      // }

      this.analytics.identify(leanUser as P.User<P.Applicant>);

      this.storage.set(STORAGE.SESSION, UserService.CURRENT_KEY, leanUser);
    } else {
      this.storage.delete(STORAGE.SESSION, UserService.CURRENT_KEY);
    }

    asapScheduler.schedule(() => {
      this.change.emit(this.current);
      this.event.emit(new Event('change') as UserEvent);
    });
  }

  /**
   * origin
   */
  get origin(): Dictionary<string> {
    try {
      const origin = JSON.parse(this.storage.get(STORAGE.COOKIE, UserService.ORIGIN_KEY));
      return origin;
    } catch (error) {
      return {};
    }
  }

  set origin(origin: Dictionary<string>) {
    if (!!origin && typeof origin === 'object') {
      const existingOrigins = this.origin;

      const allowedOrigins = pick(origin, [
        'autopilot_session_id',
        'referrer',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'gclid',
        'persistence_id',
      ]);

      const origins = merge(existingOrigins, allowedOrigins);

      this.analytics.origin = origins;

      this.storage.set(STORAGE.COOKIE, UserService.ORIGIN_KEY, JSON.stringify(origins), {
        expires: 30,
        secure: environment.production,
        path: '/',
        domain: /^http:.*\.proportunity\.local/.test(window.location.href)
          ? '.proportunity.local'
          : '.proportunity.com',
      });
    } else {
      this.storage.delete(STORAGE.COOKIE, UserService.ORIGIN_KEY);
    }
  }

  /**
   * access token
   */
  get token(): string {
    return this.storage.get(STORAGE.LOCAL, UserService.TOKEN_KEY);
  }

  set token(token: string) {
    if (token) {
      this.storage.set(
        STORAGE.LOCAL,
        UserService.TOKEN_KEY,
        token,
        // { secure: environment.production }
      );
    } else {
      this.storage.delete(STORAGE.LOCAL, UserService.TOKEN_KEY);
    }

    // this.restApi.accreditation = merge(this.restApi.accreditation, { token });
  }

  /**
   * refresh token
   */
  get refreshToken(): string {
    return this.storage.get(STORAGE.LOCAL, UserService.REFRESH_TOKEN_KEY);
  }

  set refreshToken(refreshToken: string) {
    if (refreshToken) {
      this.storage.set(
        STORAGE.LOCAL,
        UserService.REFRESH_TOKEN_KEY,
        refreshToken,
        // { secure: environment.production }
      );
    } else {
      this.storage.delete(STORAGE.LOCAL, UserService.REFRESH_TOKEN_KEY);
    }

    // this.restApi.accreditation = merge(this.restApi.accreditation, { refreshToken });
  }

  constructor(
    private restApi: RestApiService,
    private storage: StorageService,
    private analytics: AnalyticsService,
  ) {
    // init accreditation
    this.restApi.initialAccreditation({
      token: this.token,
      refreshToken: this.refreshToken,
      user: null,
    });

    // on accreditation handler
    this.restApi.identity
      .subscribe(this.onIdentity.bind(this));

    // on 401 error
    this.restApi.error
      .pipe(filter((error) => this.signedIn && error.status === 401))
      .subscribe((error) => this.signOut()
        .toPromise()
        .finally(() => this.event.emit({ type: 'forceSignOut', data: error })));
  }

  /**
   *
   */
  public update(
    update: Partial<P.User<P.Applicant>>,
    source: string = 'unknown',
  ): Observable<P.User<P.Applicant>> {
    let observable: Observable<P.User<P.Applicant>>;

    const persistenceId = this.current.persistence_id || this.analytics.proportunityPersistenceId;

    if (Object.keys(pick(update, [
      'income', 'income_extra', 'savings',
    ])).length) {
      update = merge(update, { preferences: { homes: { range: null } } });
    }

    if (this.signedIn) {
      // authenticated user update
      observable = this.restApi
        .updateUser(
          this.current.id,
          { ...update, persistence_id: persistenceId },
          this.restApi.accreditation?.token,
          { source },
        ) as Observable<P.User<P.Applicant>>;
    } else {
      // unauthenticated user update
      observable = new Observable((observer) => {
        const updatedUser: P.User<P.Applicant> = merge(this.current, {
          ...update,
          persistence_id: persistenceId,
        });
        observer.next(updatedUser);
        observer.complete();
      });
    }

    return observable
      .pipe(tap((updatedUser) => {
        this.current = updatedUser;
      }));
  }

  /**
   *
   */
  public updateJointApplicant(update: Partial<P.User<P.Applicant>>, source: string = 'unknown'): Observable<P.User<P.Applicant>> {
    const id: string = get(this.current, 'application.applicants.1.id');

    if (!id) {
      return of(this.current);
    }

    const preferences = this.current.preferences || {};

    if (Object.keys(pick(update, [
      'income', 'income_extra', 'savings',
    ])).length) {
      this
        .update({ preferences: { ...preferences, homes: { range: null } } })
        .toPromise();
    }

    return this.restApi
      .updateUser(id, update, this.restApi.accreditation?.token, { source: `${source}_joint` })
      .pipe(tap((user: P.User<P.Applicant>) => this.current = merge(this.current, { application: user.application })));
  }

  /**
   *
   */
  public updateApplication(update): Observable<P.Application> {
    const observable = this.signedIn
      ? this.restApi
        .updateApplication(this.current?.application?.id, update)
      : of(merge(this.current.application, update) as P.Application);

    return observable
      .pipe(tap((application) => this.current = merge(this.current, { application })));
  }

  /**
   *
   */
  public addJointApplicant(email: string): Observable<P.Application> {
    return this.restApi
      .addJointApplicant(this.current?.application?.id, email)
      .pipe(tap((application) => this.current = merge(this.current, { application })));
  }

  /**
   *
   */
  public removeJointApplicant(): Observable<P.Application> {
    // joint applicant id
    const id = get(this.current, 'application.applicants.1.id');
    //
    return this.restApi
      .removeJointApplicant(this.current?.application?.id, id)
      .pipe(tap((application) => this.current = assign(this.current, { application })));
  }

  /**
   *
   */
  public accredit(token: string = this.token): Observable<P.AuthAccreditation> {
    return this.restApi
      .accredit(token)
      .pipe(
        tap((accreditation) => {
          this.origin = pickBy({
            persistence_id: accreditation?.user?.persistence_id,
          }, identity);
        }),
        catchError((error) => {
          // sign out user
          this.signOut();
          // propagate error
          return throwError(error);
        }),
      );
  }

  /**
   *
   */
  public changePassword(credentials: {
    password: string;
    new_password: string;
    new_password_confirmation: string;
  }): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.restApi
      .changePassword(credentials)
      .pipe(tap((user) => this.current = user as P.User<P.Applicant>));
  }

  /**
   *
   */
  public signIn(
    credentials: P.AuthCredentials,
  ): Observable<P.User<P.Applicant>> {
    return this.restApi
      .signIn(credentials)
      .pipe(
        tap((accreditation) => {
          this.origin = pickBy({
            persistence_id: accreditation?.user?.persistence_id,
          }, identity);
        }),
        map((accreditation) => accreditation.user as P.User<P.Applicant>),
      );
  }

  /**
   *
   */
  public signOut(): Observable<P.OperationResponse> {
    return new Observable((observer) => {
      this.restApi
        .signOut(this.token)
        .toPromise()
        .finally(() => {
          // TODO: remove from here once api endpoint is live
          this.restApi.accreditation = void 0;
          //
          observer.next({ success: true });
          observer.complete();
        });
    });
  }

  /**
   *
   */
  public isFavouriteListing(listing: string): boolean {
    return !!this.getFavouriteListing(listing);
  }

  /**
   *
   */
  public getFavouriteListing(listing: string): LeanListing {
    const favouriteListing = find(this.favourites, { listing })
      || find(this.favourites, { listing: { id: listing } });
    return favouriteListing as unknown as LeanListing;
  }

  /**
   * add to shortlist
   */
  public favourite(data: {
    listing?: string | P.Listing,
    url: string
  }): Observable<LeanFavourite> {
    return this.restApi
      .createEnquiry(
        this.current?.application?.id,
        data as Partial<P.Enquiry>,
        this.token,
      )
      .pipe(
        map(mapToLeanFavourite),
        map((a) => a as LeanFavourite),
        tap(this.addToFavourites.bind(this)),
      );
  }

  /**
   * 'update' in shortlist
   */
  public updateFavourite(id: string, update: Partial<P.Enquiry>): Observable<LeanFavourite> {
    return this.restApi
      .updateEnquiry(id, update)
      .pipe(
        map(mapToLeanFavourite),
        map((a) => a as LeanFavourite),
        tap(this.updateInFavourites.bind(this)),
      );
  }

  /**
   * 'remove' from shortlist
   */
  public unFavourite(enquiry: string): Observable<LeanFavourite> {
    return this.restApi
      .updateEnquiry(
        enquiry,
        { status: 'Canceled' },
        this.token,
      )
      .pipe(
        map(mapToLeanFavourite),
        map((a) => a as LeanFavourite),
        tap(this.updateInFavourites.bind(this)),
      );
  }

  /**
   *
   */
  private addToFavourites(enquiry: LeanFavourite): void {
    const shortlist = [...this.current.application.shortlist, enquiry] as P.Enquiry[];
    const update: Partial<P.User<P.Applicant>> = { application: { shortlist } };
    this.current = merge(this.current, update);
  }

  /**
   *
   */
  private updateInFavourites(enquiry: LeanFavourite): void {
    const shortlist = [...this.current.application.shortlist];
    const index = findIndex(shortlist, { id: enquiry.id });
    if (index > -1) {
      shortlist[index] = enquiry as P.Enquiry;
      // shortlist.splice(index, 1);
      const update: Partial<P.User<P.Applicant>> = { application: { shortlist } };
      this.current = merge(this.current, update);
    }
  }

  /**
   *
   */
  private onIdentity(accreditation: P.AuthAccreditation): void {
    this.token = accreditation?.token;
    this.refreshToken = accreditation?.refreshToken;
    this.current = accreditation?.user as P.User<P.Applicant>;

    const signedIn = !!accreditation;

    this.event.emit(new Event(signedIn ? 'signIn' : 'signOut') as UserEvent);
  }

  async qualifierBreakdown(application: Partial<P.Application>): Promise<QualifierBreakdown> {
    // deposit
    const minDeposit = await this.minDeposit();
    const hasBelowMinDeposit = application.deposit < minDeposit;

    // income
    const minIncome = application?.applicants?.length > 1
      ? 40000
      : 30000;
    const hasBelowMinIncome = application.income < minIncome;

    // residency
    const mainApplicantResidenceTypeIndex = residencyOptions
      .map((option) => option.value)
      .indexOf((application.applicants[0] as any)?.residence_type);

    const hasEligibleResidenceType = mainApplicantResidenceTypeIndex > 0
      && mainApplicantResidenceTypeIndex < 4;
    const hasIneligibleResidenceType = !hasEligibleResidenceType;

    const productInterest = (application.qualifier as any)?.products?.answer;

    const hasProductInterest = typeof productInterest === 'string';

    const isNotInterrestedInMortgageBooster = hasProductInterest
      && productInterest
        .split(',')
        .indexOf('mortgage_booster') === -1;

    return {
      hasBelowMinDeposit,
      hasIneligibleResidenceType,
      hasBelowMinIncome,
      isNotInterrestedInMortgageBooster,
    };
  }

  private async minDeposit(): Promise<number> {
    return await this.isInHighDemandCityOutsideLondon() ? 7500 : 12500;
  }

  /**
   * If the lead is not signed up yet we can't calculate segment on server because their application doesn't exist on server.
   * It's okay to duplicate this logic in terms of data consistency since the lead can't be qualified before sign up.
   * It would be great to have this code in a libary to share between server and client, though.
   */
  private async _segment(): Promise<P.Segment> {
    // qualifing data
    const { application } = this.current;
    const { qualifier } = application;

    const {
      hasBelowMinDeposit,
      hasBelowMinIncome,
      hasIneligibleResidenceType,
    } = await this.qualifierBreakdown(this.current.application);

    const minDeposit = await this.minDeposit();
    const hasAboveMinDeposit = !hasBelowMinDeposit;

    const hasNegligibleDeposit = application.deposit < (minDeposit / 2);
    const hasAboveMinIncome = !hasBelowMinIncome;

    const hasEligibleResidenceType = !hasIneligibleResidenceType;

    // home finding
    const homeStatus = qualifier?.home?.answer;

    const hasFoundHome = homeStatus === 'Yes';

    const hasOfferAccepted = !!application.my_home;

    if ((qualifier as any)?.segment === 'Dead') {
      return 'Dead';
    }

    let segment: P.Segment = (qualifier as any)?.segment || 'Low - Nurture';

    if (hasNegligibleDeposit
      || hasBelowMinIncome
      || hasIneligibleResidenceType) {
      segment = 'Low - Recycle';
    }

    if (hasAboveMinDeposit && hasAboveMinIncome && hasEligibleResidenceType) {
      segment = 'Medium';

      if (hasFoundHome || hasOfferAccepted) {
        segment = 'High';
      }
    }

    return segment;
  }

  public async segment(): Promise<P.Segment> {
    if (this.signedIn) {
      const isInHighDemand = await this.isInHighDemandCityOutsideLondon();
      const response = await this.restApi.getSegment(this.current.application.id, isInHighDemand).toPromise();
      return response.segment;
    }
    return this._segment();
  }

  private _isInHighDemandCityOutsideLondon: boolean;

  async isInHighDemandCityOutsideLondon(): Promise<boolean> {
    if (this._isInHighDemandCityOutsideLondon !== undefined) {
      return this._isInHighDemandCityOutsideLondon;
    }
    try {
      // get location from ip
      const response = await fetch('https://ipapi.co/json/');
      const location = await response.json();
      // if outside London in high demand area
      const isInHighDemandCityOutsideLondon = [
        'Birmingham', 'Manchester', 'Bristol', 'Leeds', 'Croydon', 'Liverpool', 'Nottingham', 'Cardiff',
        'Coventry', 'Sheffield', 'Reading', 'Slough', 'Watford', 'Southampton', 'Bradford', 'Wolverhampton',
        'Leicester', 'Salford', 'Plymouth',
      ].indexOf(location?.city) > -1;

      this._isInHighDemandCityOutsideLondon = isInHighDemandCityOutsideLondon;
      return isInHighDemandCityOutsideLondon;
    } catch (e) {
      this._isInHighDemandCityOutsideLondon = false;
      return false;
    }
  }
}
